import { useNavigation } from "@react-navigation/native"
import { StackNavigationProp } from "@react-navigation/stack"
import { auth, Button, FormikTextInput, logger, useAlert, useTheme } from "capsule"
import {
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
} from "capsule/features/Firebase"
import { Formik, FormikConfig } from "formik"
import React, { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Platform, StyleSheet, View } from "react-native"
import * as Yup from "yup"

import { INPUT_HEIGHT } from "../features/config/Constants"
import { LoginNS } from "../features/i18n/constants"
import { useCode } from "../features/Providers/CodeProvider"
import { useMfaResolver } from "../features/Providers/MfaProvider"
import { IMainParamList } from "../screens/MainScreen/MainNavigator"

export interface LoginFormType {
  email: string
  password: string
}
const LoginForm = () => {
  const {
    dimensions: { spacing },
    colors: {
      surface: { textInput },
    },
  } = useTheme()
  const navigation = useNavigation<StackNavigationProp<IMainParamList, "Account">>()

  const { setResolver } = useMfaResolver()
  const { showSnack } = useAlert()

  const recaptchaRef = useRef(null)
  const { setCode } = useCode()
  const { t } = useTranslation(LoginNS)
  const [loading, setLoading] = useState(false)
  const onHandleSubmit = async ({ email, password }) => {
    setLoading(true)
    setCode?.(undefined)
    try {
      await auth().signInWithEmailAndPassword(email, password)
    } catch (error) {
      const { code } = error

      if (code === "auth/multi-factor-auth-required") {
        let recaptchaVerifier
        if (Platform.OS === "web" && recaptchaRef.current) {
          // @ts-ignore
          recaptchaVerifier = auth().RecaptchaVerifier(recaptchaRef.current, {
            size: "invisible",
            callback(response) {
              logger(response)
            },
          })
          await recaptchaVerifier.render()
        }
        try {
          const resolverTemp = getMultiFactorResolver(auth(), error)

          if (resolverTemp.hints.length === 0) {
            logger("Resolver hints is empty")
            showSnack({ message: t("errors.unknown") })
            return
          }
          if (resolverTemp.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
            const hint = resolverTemp.hints[0]
            const phoneAuthProvider = new PhoneAuthProvider(auth())
            const verificationId = await phoneAuthProvider.verifyPhoneNumber(
              {
                multiFactorHint: hint,
                session: resolverTemp.session,
              },
              Platform.OS === "web" ? recaptchaVerifier : undefined,
            ) // triggers the message to the user

            setResolver?.(resolverTemp)
            navigation.navigate("MfaLogin", { verifId: verificationId })
          }
          return
        } catch (e) {
          logger("MFA resolver error: ", e)
          showSnack({ message: t("errors.mfaError") })
          return
        }
      }
      logger("Login error: ", error)
      switch (code) {
        case "auth/user-not-found":
        case "auth/wrong-password":
          showSnack({ message: t("errors.invalidCredentials") })
          break
        default:
          showSnack({ message: t("errors.unknown") })
      }
    } finally {
      setLoading(false)
    }
  }
  const formikConfig: FormikConfig<LoginFormType> = {
    validateOnMount: true,
    onSubmit: onHandleSubmit,
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().email().required(t("errors.emailNeeded")),
      password: Yup.string().required(t("errors.passwordNeeded")),
    }),
  }

  const { inputStyle, section } = useMemo(
    () => ({
      inputStyle: { backgroundColor: textInput, minHeight: INPUT_HEIGHT },
      section: {
        gap: spacing,
      },
    }),
    [spacing, textInput],
  )

  return (
    <>
      <Formik {...formikConfig}>
        {({ handleSubmit, isValid }) => (
          <View style={styles.formContainer}>
            <View style={section}>
              <FormikTextInput
                name="email"
                changeUnderline={false}
                autoCapitalize="none"
                label={t("email")}
                style={inputStyle}
                testID="email-input"
              />
              <FormikTextInput
                name="password"
                secureTextEntry
                changeUnderline={false}
                autoCapitalize="none"
                autoComplete="off"
                autoCorrect={false}
                label={t("password")}
                style={inputStyle}
                testID="password-input"
              />
            </View>
            <Button
              testID="input-screen-save-btn"
              style={styles.button}
              onPress={() => {
                handleSubmit()
              }}
              disabled={!isValid || loading}
              loading={loading}
            >
              {t("signUpButton")}
            </Button>
          </View>
        )}
      </Formik>
      {Platform.OS === "web" ? <div id="recaptcha-container" ref={recaptchaRef} /> : undefined}
    </>
  )
}

const styles = StyleSheet.create({
  formContainer: {
    width: "100%",
  },
  button: {
    width: "100%",
    marginTop: 16,
  },
})

export default LoginForm
